import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label } from '@pisaka/ui';
import { useAuth, useRegister } from '../useAuth';
import { privacyPageUrl } from '../../../helpers/urls';

type RegisterContainerType = {
  onCloseModal: () => void;
};

export const RegisterContainer: FC<RegisterContainerType> = ({ onCloseModal }) => {
  const { t } = useTranslation();
  const { isLoadingRegister, register, registerError, setRegisterError } = useRegister();
  const [consentChecked, setConsentChecked] = useState(false);
  const {
    email, emailError, isValidEmail,
    password, passwordError, isValidPassword,
    name, onChangeEmail, onChangePassword, onChangeName,
  } = useAuth();

  const onRegister = async () => {
    try {
      await register(name, email, password);
      onCloseModal();
      setRegisterError(undefined);
    } catch (error) {
      if (error instanceof Error) setRegisterError(error.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label>{t('auth.nameLabel')}</Label>
        <Input
          value={name}
          onChange={(e) => onChangeName(e.target.value)}
          placeholder={t('auth.namePlaceholder')}
        />
      </div>
      <div className="space-y-1">
        <Label>{t('auth.emailLabel')}</Label>
        <Input
          type="email"
          value={email}
          onChange={(e) => onChangeEmail(e.target.value)}
          placeholder={t('auth.emailPlaceholder')}
        />
        {emailError && <p className="text-sm text-destructive">{emailError}</p>}
      </div>
      <div className="space-y-1">
        <Label>{t('auth.passwordLabel')}</Label>
        <Input
          type="password"
          value={password}
          onChange={(e) => onChangePassword(e.target.value)}
          placeholder={t('auth.passwordPlaceholder')}
        />
        {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
      </div>
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={consentChecked}
          onChange={(e) => setConsentChecked(e.target.checked)}
          className="mt-0.5 shrink-0"
        />
        <span className="text-xs text-muted-foreground">
          {t('auth.consentPrefix')}{' '}
          <a href={privacyPageUrl} target="_blank" rel="noreferrer" className="underline hover:text-foreground">
            {t('auth.consentLink')}
          </a>
        </span>
      </label>
      {registerError && <p className="text-sm text-destructive">{registerError}</p>}
      <Button
        className="w-full bg-gradient-accent hover:bg-accent shadow-accent text-primary-foreground"
        onClick={onRegister}
        disabled={isLoadingRegister || !name.length || !isValidEmail || !isValidPassword || !consentChecked}
      >
        {isLoadingRegister ? '...' : t('auth.registerButton')}
      </Button>
    </div>
  );
};
